import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type PrivateWarehouseMapping = {
  id: string;
  isActive: boolean;
  createdAt: string;
  buyerTenantId: string;
  buyerTenantName: string;
  buyerLocationId: string;
  buyerLocationName: string;
  warehouseTenantId: string;
  warehouseTenantName: string;
  warehouseLocationId: string;
  warehouseLocationName: string;
};

export type ProductMapping = {
  id: string;
  conversionFactor: string;
  companyProductId: string;
  companyProductName: string;
  companyProductBarcode: string | null;
  warehouseProductId: string;
  warehouseProductName: string;
  warehouseProductBarcode: string | null;
};

/** Every private_warehouse location across tenants (super admin). */
export function useAdminPrivateWarehouses() {
  return useQuery({
    queryKey: ['admin', 'private-warehouses'],
    queryFn: () =>
      api.get<{ data: { id: string; name: string; isActive: boolean; tenantId: string; tenantName: string }[] }>(
        '/admin/private-warehouses',
      ),
  });
}

export function useAdminPrivateWarehouseMappings(tenantId: string) {
  return useQuery({
    queryKey: ['admin', 'private-warehouse-mappings', tenantId],
    queryFn: () =>
      api.get<{ data: PrivateWarehouseMapping[] }>(`/admin/tenants/${tenantId}/private-warehouse-mappings`),
    enabled: !!tenantId,
  });
}

export function useCreatePrivateWarehouseMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { buyerLocationId: string; warehouseLocationId: string }) =>
      api.post('/admin/private-warehouse-mappings', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'private-warehouse-mappings'] }),
  });
}

export function useDeactivatePrivateWarehouseMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/private-warehouse-mappings/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'private-warehouse-mappings'] }),
  });
}

export function useAdminProductSearch(tenantId: string, q: string) {
  return useQuery({
    queryKey: ['admin', 'product-search', tenantId, q],
    queryFn: () =>
      api.get<{ data: { id: string; name: string; sku: string | null; primaryBarcode: string | null }[] }>(
        `/admin/tenants/${tenantId}/product-search`,
        { q },
      ),
    enabled: !!tenantId && q.trim().length >= 2,
  });
}

export function useAdminProductMappings(buyerTenantId: string, warehouseTenantId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'warehouse-product-mappings', buyerTenantId, warehouseTenantId],
    queryFn: () =>
      api.get<{ data: ProductMapping[] }>('/admin/warehouse-product-mappings', { buyerTenantId, warehouseTenantId }),
    enabled: enabled && !!buyerTenantId && !!warehouseTenantId,
  });
}

export function useAdminBarcodePreview(buyerTenantId: string, warehouseTenantId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'warehouse-product-mappings', 'barcode', buyerTenantId, warehouseTenantId],
    queryFn: () =>
      api.get<{ data: { companyProductName: string; warehouseProductName: string; barcode: string }[] }>(
        '/admin/warehouse-product-mappings/barcode-preview',
        { buyerTenantId, warehouseTenantId },
      ),
    enabled: enabled && !!buyerTenantId && !!warehouseTenantId,
  });
}

export function useUpsertProductMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { companyProductId: string; warehouseProductId: string; conversionFactor: number }) =>
      api.post('/admin/warehouse-product-mappings', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'warehouse-product-mappings'] }),
  });
}

export function useDeleteProductMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/warehouse-product-mappings/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'warehouse-product-mappings'] }),
  });
}
